// 🎨 «컬러컷» 무대 — 화면 전체가 원색 한 장이고, 장면이 바뀌면 다음 색이 아래→위로 쓸려 올라온다(원 설계서 3-1 ColorCut).
//   그 위에 큰 제목이 한 자씩 튀어나오고(Kinetic) · 두 줄이면 둘째 줄이 띠 위로 늦게 올라오고(PairCopy) · 체크리스트 끝에 체크 도장이 «쾅»(Stamp).
//   정본 지도 docs/01-plan/features/motion-style-pack.md «컬러컷» 절. 모션 브리핑(`MotionBriefStage`)과 같은 자리에 꽂히는 형제 무대다.
//   ★장표 10종은 «안 내용»으로 그대로 쓴다★ — 글자 자리는 모션 브리핑과 같은 계산식(`briefStageFit`)으로 정해 `SlideStageInsetContext` 로 내려보낸다
//     → 칸 계획·촘촘히·한 줄 라벨·자막 띠 비켜서기 규칙과 경계 검사 표식이 그대로 따라온다. 채팅 편집·장표 편집 계약·직접 수정·EF 는 이 파일을 모른다.
//   도형은 없다(색면이 곧 무대) — 경계 검사용 `data-brief-shape` 는 «글자 자리 전체(세이프존 core · 자막 띠 위)»를 가리키는 투명 상자다.
//   순수 렌더: 같은 (프레임·장면·앞 장면·장면 번호) = 같은 그림. 난수·시계·프레임 간 상태 없음.
import React from 'react';
import { AbsoluteFill, useVideoConfig } from 'remotion';
import { safeInsetPx } from '../../lib/safe-zone';
import { EASE_ENTER, UI_SPRING, lin, springStep, win } from '../../lib/motion-kit/spring';
import { briefStageFit, type BriefCaption, type MotionBriefStageProps } from './MotionBriefStage';
import {
  BRIEF_COMPACT, BRIEF_CONTENT_FADE_SEC, BRIEF_CONTENT_TAIL_SEC, BRIEF_PAD, BRIEF_SUB_COL_GAP, BRIEF_TITLE_GAP, briefContentHeight, briefFullTextSubCols, briefGlueLastWords, briefLineCount, briefTextOn, briefTextWidth, type BriefSlide,
} from './motion-brief-shape';
import {
  COLOR_CUT_WIPE_LONG_SEC, COLOR_CUT_WIPE_SEC, colorCutContentColors, colorCutPalette, colorCutScene, colorCutWipe,
} from './color-cut-palette';
import { SlideStageInsetContext } from './stage-inset';
import { useBalancedTitle } from './title-balance';
import { GlueText, KeywordText, type KeywordEmphasisId } from './motion';
import { FW, KO_TEXT, LINE_HEIGHT, T, type AtmosphereColors } from './tokens';
import { titleCueLeadSec } from "../../lib/templates/word-cues";

/** ⏱🔤 항목 낱말 신호 중 가장 늦은 것(초) — 체크 도장은 마지막 항목이 켜진 뒤에 찍는다. 신호가 없으면 -∞(종전 식만 남는다). */
const lastCueSec = (items: ReadonlyArray<number | null> | undefined, fps: number): number => {
  const xs = (items ?? []).filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
  return xs.length && fps > 0 ? Math.max(...xs) / fps : Number.NEGATIVE_INFINITY;
};

/** 무대 props — 모션 브리핑과 같은 모양 + 장면 번호(전체 씬 번호 · 0부터). 번호가 없으면 장표 종류별 고정 색(차선). */
export interface ColorCutStageProps extends MotionBriefStageProps {
  sceneIndex?: number;
}

/** 한 자씩 등장 — 글자 사이 지연(초) · 제목 전체가 이 안에 다 시작한다(초) · 한 글자 스프링이 자리 잡는 시간(초). */
export const COLOR_CUT_CHAR_GAP_SEC = 0.03;
export const COLOR_CUT_CHAR_SPREAD_SEC = 0.7;
export const COLOR_CUT_CHAR_SETTLE_SEC = 0.45;
/** 대구 카피 — 둘째 줄은 첫 줄 마지막 글자가 시작하고 이만큼 뒤에 올라온다(초 · 원 설계서 «반박 뒤»). */
export const COLOR_CUT_PAIR_DELAY_SEC = 0.25;
/** 체크 도장 — 2.4배에서 1배로 «쾅»(원 설계서 3-1 Stamp). 글자는 없다(편집 못 하는 글자를 화면에 박지 않는다). */
export const COLOR_CUT_STAMP_FROM = 2.4;
/** 체크 도장 자리 — 안 내용 오른쪽 여백이 이 폭(1920 기준 px) 이상 남을 때만 찍는다(글자와 겹치지 않는다 · 좁으면 찍지 않는다). */
export const COLOR_CUT_STAMP_ROOM = 150;
/** 큰 숫자 «쾅» — 캔버스 밖으로 나가지 않는 배율만. */
export const COLOR_CUT_SLAM_FROM = 1.12;
/** 크게 채우기 상한 — 도형이 없는 색면에서는 제목+안 내용 묶음을 글자 자리에 찰 때까지 키운다(본문 56px → 최대 100px). */
export const COLOR_CUT_FILL_MAX = 1.8;
/** 크게 채우기 여유 — 장표는 등장 뒤에도 천천히 다가온다(모션 타이포 1.06배 · 나머지 1.035배). 그만큼 덜 키워 끝 프레임에도 글자 자리 안. */
export const COLOR_CUT_FILL_MARGIN = 1.07;

export interface ColorCutFill {
  /** 묶음 배율(1 ~ `COLOR_CUT_FILL_MAX`). */
  s: number;
  /** 묶음을 옆으로 옮길 거리(px) — 글자 자리 가운데로(도장 칸을 비우면 왼쪽으로 반 칸). 제목·안 내용 공통. */
  dx: number;
  /** 제목 띠를 아래로 옮길 거리(px · 배율 기준점 = 띠 윗변 가운데). */
  titleDy: number;
  /** 안 내용을 옮길 거리(px · 배율 기준점 = 안 내용 자리 가운데). */
  bodyDy: number;
  /** 키운 뒤 안 내용 자리(px) — 도장 자리·경계 테스트가 쓴다. */
  rect: { x: number; y: number; w: number; h: number };
}

/**
 * 크게 채우기 — 제목 띠(있으면)와 안 내용 자리를 한 묶음으로 보고, 글자 자리(`field`) 안에 찰 때까지 같은 배율로 키워 가운데에 놓는다.
 *   묶음은 글자 자리 밖으로 나가지 않는다(가로 = 안 내용 폭과 제목 글자 폭 중 큰 쪽 · 세로 = 띠 + 간격 + 안 내용). 순수 함수.
 */
export function colorCutFill(field: { left: number; top: number; width: number; height: number }, rect: { x: number; y: number; w: number; h: number }, band: { top: number; height: number; textWidth: number } | null, gapPx: number, max = COLOR_CUT_FILL_MAX): ColorCutFill {
  const gw = Math.max(rect.w, band ? band.textWidth : 0);
  const gh = rect.h + (band ? band.height + gapPx : 0);
  const s = gw > 0 && gh > 0 ? Math.max(1, Math.min(max, field.width / gw / COLOR_CUT_FILL_MARGIN, field.height / gh / COLOR_CUT_FILL_MARGIN)) : 1;
  const top0 = field.top + (field.height - s * gh) / 2;
  const bodyTop = top0 + (band ? s * (band.height + gapPx) : 0);
  const cy = rect.y + rect.h / 2;
  const cx = field.left + field.width / 2;
  return {
    s, dx: cx - (rect.x + rect.w / 2), titleDy: band ? top0 - band.top : 0, bodyDy: bodyTop + (s * rect.h) / 2 - cy,
    rect: { x: cx - (s * rect.w) / 2, y: bodyTop, w: s * rect.w, h: s * rect.h },
  };
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const chars = (t: string): string[] => Array.from(t);

/** 제목 글자 수(띄어쓰기 제외)로 본 «한 자씩» 시간표 — 글자 사이 지연과 다 끝나는 시각(초 · 시작 0 기준). */
export function colorCutKineticPlan(text: string): { gap: number; lastStart: number; done: number } {
  const n = chars(text).filter((c) => c.trim()).length;
  const gap = n > 1 ? Math.min(COLOR_CUT_CHAR_GAP_SEC, COLOR_CUT_CHAR_SPREAD_SEC / (n - 1)) : 0;
  const lastStart = Math.max(0, n - 1) * gap;
  return { gap, lastStart, done: lastStart + COLOR_CUT_CHAR_SETTLE_SEC };
}

/** 줄 끝에 홀로 남으면 안 되는 수사 어절(«세 / 가지») — 경계 검사 ④ 와 같은 목록. */
const NUMERAL_END = /^(?:한|두|세|네|다섯|여섯|일곱|여덟|아홉|열|스무|몇|여러|\d+(?:[.,]\d+)?)$/;

/**
 * 제목을 두 줄로 고르게 나눈다 — 글자 폭 모델로 두 줄 폭이 가장 비슷한 어절 경계.
 *   마지막 줄에 어절 하나만 남기지 않는다(어절 ≥3) · 첫 줄이 수사로 끝나지 않는다. 어절이 하나뿐이거나 나눌 자리가 없으면 null.
 */
export function colorCutBalancedSplit(text: string, fontPx: number): [string, string] | null {
  const words = text.trim().split(/[ \t\u00A0]+/).filter(Boolean);
  let best: [string, string] | null = null, bestW = Infinity;
  for (let i = 1; i < words.length; i++) {
    if (words.length >= 3 && words.length - i < 2) continue;
    if (NUMERAL_END.test(words[i - 1])) continue;
    const a = words.slice(0, i).join(' '), b = words.slice(i).join(' ');
    const w = Math.max(briefTextWidth(a, fontPx, true), briefTextWidth(b, fontPx, true));
    if (w < bestW) { best = [a, b]; bestW = w; }
  }
  return best;
}

/** 두 줄 제목을 어디서 나눌까(대구 카피 · 위 제목 띠) — 폭에 두 줄로 접히는 제목만 나눈다(한 줄·세 줄 이상은 null) · 두 줄 다 폭 안. */
export function colorCutPairSplit(text: string, fontPx: number, widthPx: number): [string, string] | null {
  if (briefLineCount(text, fontPx, widthPx) !== 2) return null;
  const two = colorCutBalancedSplit(text, fontPx);
  return two && Math.max(briefTextWidth(two[0], fontPx, true), briefTextWidth(two[1], fontPx, true)) <= widthPx ? two : null;
}

/**
 * 안 내용 자리를 글 높이에 맞춰 조인다 — 모션 브리핑의 도형은 표의 높이(화면의 40~64%)를 지키느라 글보다 훨씬 클 수 있는데,
 *   도형이 없는 색면에서는 그 빈 높이가 «크게 채우기»를 막는다. 가운데는 그대로 두고 높이만 (어림 높이 × 1.06 + 위아래 여백)으로 줄인다.
 *   글이 이미 자리에 꽉 찼거나(배율 < 1) 자막 띠를 품는 자리(아래 여백 0)·어림이 없는 종류(사진)는 그대로.
 */
export function colorCutTightRect<R extends { x: number; y: number; w: number; h: number }>(rect: R, padBottom: number, contentScale: number, estHeight: number | null, k: number, estWidth?: number | null): R {
  if (estHeight == null || contentScale < 1 || padBottom === 0) return rect;
  const h = Math.min(rect.h, Math.ceil(estHeight * k * 1.06 + 2 * BRIEF_PAD.y * k));
  // 폭도 글에 맞춘다(목록) — 짧은 항목 목록이 넓은 자리 왼쪽에 작게 붙어 있으면 «크게 채우기»가 폭에 막힌다.
  const w = typeof estWidth === 'number' ? Math.min(rect.w, Math.ceil(estWidth * k + 2 * BRIEF_PAD.x * k)) : rect.w;
  return { ...rect, x: rect.x + (rect.w - w) / 2, w, y: rect.y + (rect.h - h) / 2, h };
}

/** 체크리스트 안 내용 폭 어림(1920 기준 px) — 체크 칸(52) + 간격(24) + 가장 긴 항목(글자 폭 모델 · 10% 여유 포함). 다른 종류는 null(자리 폭 그대로). */
export function colorCutContentWidth(slide: BriefSlide): number | null {
  if (slide.type !== 'checkList') return null;
  const items = Array.isArray(slide.content?.items) ? slide.content.items.map(str) : [];
  return items.length ? 52 + 24 + Math.max(...items.map((it) => briefTextWidth(it, T.body))) : null;
}

/**
 * 제목 장면(fullText) 큰 제목의 마지막 두 어절을 붙인 장표 — 그리기와 자리 계산이 같은 글을 쓴다. 붙일 폭 = 글자 자리 가장 넓은 폭(세이프존 core − 여백).
 *   다른 종류·제목 없음은 그대로 돌려준다(같은 객체).
 */
export function colorCutGlueTitle(slide: BriefSlide, W: number, reserveRightPx = 0): BriefSlide {
  const title = str(slide.content?.title);
  if (slide.type !== 'fullText' || !title.trim()) return slide;
  const k = W / 1920;
  const core = safeInsetPx('16:9', W, Math.round(W * 9 / 16), 'core');
  const glued = briefGlueLastWords(title, T.hero * k, W - core.left - core.right - reserveRightPx - 2 * BRIEF_PAD.x * k);
  return glued === title ? slide : { type: slide.type, content: { ...(slide.content ?? {}), title: glued } };
}

/** 한 자씩 스프링 — 어절은 통째로 줄바꿈되고(어절 안에서 끊기지 않는다) 글자마다 아래에서 튀어 올라온다. 다 끝나면 `children`(강조 낱말 포함 완성 글)을 그대로 낸다. */
const KineticChars: React.FC<{ text: string; tSec: number; at: number; children: React.ReactNode }> = ({ text, tSec, at, children }) => {
  const plan = colorCutKineticPlan(text);
  if (tSec >= at + plan.done) return <>{children}</>;
  let ci = -1;
  return (
    <>
      {text.split(/( +|\n)/).map((tok, wi) => {
        if (!tok) return null;
        if (/^( +|\n)$/.test(tok)) return tok;
        return (
          <span key={wi} style={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
            {chars(tok).map((ch, i) => {
              if (!ch.trim()) return ch; // 붙임 띄어쓰기(U+00A0)는 그대로
              ci++;
              const tau = tSec - at - ci * plan.gap;
              const p = springStep(tau, UI_SPRING.stiffness, UI_SPRING.damping, UI_SPRING.mass);
              return (
                <span key={i} style={{ display: 'inline-block', opacity: lin(tau, 0, 0.08), transform: `translateY(${(0.4 * (1 - p)).toFixed(4)}em) scale(${(0.5 + 0.5 * p).toFixed(4)})` }}>{ch}</span>
              );
            })}
          </span>
        );
      })}
    </>
  );
};

/** 대구 카피 둘째 줄 — 글자색 띠 위에 장면색 글자(둘의 대비는 팔레트가 ≥4.5 로 잠근다). 아래에서 올라오며 켜진다. */
const PairBand: React.FC<{ tSec: number; at: number; bandColor: string; textColor: string; padEm?: number; children: React.ReactNode }> = ({ tSec, at, bandColor, textColor, padEm = 0.28, children }) => {
  const p = EASE_ENTER(lin(tSec, at, at + 0.3));
  return (
    <span data-colorcut-pair="" style={{ display: 'inline-block', whiteSpace: 'nowrap', background: bandColor, color: textColor, padding: `0 ${padEm}em`, borderRadius: '0.12em',
      opacity: p, transform: `translateY(${(0.6 * (1 - p)).toFixed(4)}em)` }}>{children}</span>
  );
};

/** 글자 자리 전체(세이프존 core · 자막 띠 윗변 24px 위까지 · 프레젠터 예약 컬럼 제외) — 경계 검사가 «글자 ⊂ 이 상자»를 잰다. */
function textField(W: number, H: number, core: { top: number; bottom: number; left: number; right: number }, reserveRightPx: number, caption?: BriefCaption) {
  const k = W / 1920;
  const bottom = caption ? Math.max(core.bottom, H - caption.top + Math.round(24 * k)) : core.bottom;
  return { left: core.left, top: core.top, width: W - core.left - core.right - reserveRightPx, height: H - core.top - bottom };
}

export const ColorCutStage: React.FC<ColorCutStageProps> = ({ f, slide: slideIn, prevSlide, sceneFrames, brand, reserveRightPx = 0, captionTopPx, captionBottomPx, captionWidthPx, titleColors, opening = false, sceneIndex, wordCues, children }) => {
  const { width: W, height: H, fps } = useVideoConfig();
  const caption = typeof captionTopPx === 'number' && typeof captionBottomPx === 'number' ? { top: captionTopPx, bottom: captionBottomPx, width: captionWidthPx } : undefined;
  // 제목 장면은 무대가 큰 제목을 그리며 마지막 두 어절을 붙인다 — 자리 계산도 «붙인 글»로 해야 줄 수가 맞는다
  //   (붙인 덩이가 길어 한 줄이 늘었는데 자리는 붙이기 전 높이로 잡아 10px 넘쳤다 · 10-02 정본 검사 topic2-v2).
  const slide = colorCutGlueTitle(slideIn, W, reserveRightPx);
  const { k, core, cin, avail, cur } = briefStageFit(slide, W, H, reserveRightPx, caption, true);
  const palette = React.useMemo(() => colorCutPalette(brand), [brand]);
  const scene = colorCutScene(palette, { sceneIndex, type: slide.type, prevType: prevSlide?.type });
  const t = Math.max(0, f) / fps;
  // 쓸림 — 앞이 장표면 0.18초(팔레트가 밝기 차를 90 안으로 묶는다) · 앞이 영상·그림이면 0.8초(상대 밝기를 모른다) · 첫 장면은 켜진 채로(검은 프레임 0).
  const wipeSec = opening ? 0 : prevSlide ? COLOR_CUT_WIPE_SEC : COLOR_CUT_WIPE_LONG_SEC;
  const p = opening ? 1 : colorCutWipe(t, wipeSec);
  // 안 내용은 쓸림이 끝난 뒤 들어오고, 다음 쓸림 0.1초 전에 빠진다 — 쓸리는 동안에는 색면만 보인다.
  const lead = wipeSec + (opening ? 0 : 0.04);
  const endSec = typeof sceneFrames === 'number' && sceneFrames > 0 ? sceneFrames / fps - BRIEF_CONTENT_TAIL_SEC : undefined;
  const alpha = win(t, lead, endSec !== undefined && endSec > lead + 0.5 ? endSec : undefined, BRIEF_CONTENT_FADE_SEC);
  const poster = slide.type === 'posterStack';
  const field = textField(W, H, core, reserveRightPx, caption);
  const tight = (fit: typeof cur, fitSlide: BriefSlide) => colorCutTightRect(fit.rect, fit.padBottom, fit.contentScale, briefContentHeight(fitSlide, (fit.rect.w - 2 * BRIEF_PAD.x * k) / k, fit.compact === true, fit.subCols === true), k, colorCutContentWidth(slide));
  // 키울 때는 «안쪽 글 상자»(자리에서 여백을 뺀 것)를 글자 자리에 맞춘다 — 여백은 글이 없어 글자 자리 밖으로 나가도 된다.
  const innerOf = (r: { x: number; y: number; w: number; h: number }, padBottom: number) => ({ x: r.x + BRIEF_PAD.x * k, y: r.y + BRIEF_PAD.y * k, w: Math.max(1, r.w - 2 * BRIEF_PAD.x * k), h: Math.max(1, r.h - BRIEF_PAD.y * k - padBottom) });
  const band = cin.band;
  const pair = band ? colorCutPairSplit(band.text, band.fontPx, band.width) : null;
  // 항목은 아래에서 올라오며 들어온다 — 키운 만큼 그 낙차도 커지므로 위아래 16px 씩은 비워 두고 맞춘다(10-02 전 프레임 검사: 마지막 항목 1프레임 7px).
  const fillField = { ...field, top: field.top + 16 * k, height: field.height - 32 * k };
  // 크게 채우기(위아래) — 사진 장표(액자·포스터)는 그대로 둔다(그림은 이미 자리를 채운다).
  // 사진 장표(액자·포스터)는 그대로 둔다(그림은 이미 자리를 채운다).
  const noFill = poster || slide.type === 'mediaFocus';
  const stackTo = noFill ? cur.rect : tight(cur, cin.fitSlide);
  const bandTextW = band ? Math.min(band.width, Math.max(...(pair ?? [band.text]).map((ln) => briefTextWidth(ln, band.fontPx, true))) + band.fontPx * 0.5) : 0;
  // 모션 타이포(장면 메움 포함)도 그대로 — 글자가 이미 132px 이고, 글자마다 아래에서 떨어지는 낙차가 배율만큼 커져 자리 밖으로 비쳤다(10-02 정본 검사 topic2 2px).
  // 모션 타이포(장면 메움 포함)는 가운데로 옮기기만 하고 키우지 않는다(배율 1) — 글자가 이미 132px 이고, 글자마다 아래에서 떨어지는
  //   낙차가 배율만큼 커져 자리 밖으로 비쳤다(10-02 정본 검사 topic2 2px). 옮기기는 남긴다 — 빼면 자막 띠 쪽으로 10px 넘쳤다(topic2-v2).
  const stackFill = noFill
    ? { s: 1, dx: 0, titleDy: 0, bodyDy: 0, rect: stackTo }
    : colorCutFill(fillField, innerOf(stackTo, cur.padBottom), band ? { top: band.top, height: band.height, textWidth: bandTextW } : null, Math.round(BRIEF_TITLE_GAP * k), slide.type === 'kineticTypo' ? 1 : COLOR_CUT_FILL_MAX);
  const fit = cur, to = stackTo, fill = stackFill;
  const inner = innerOf(to, fit.padBottom);
  const origin = { x: inner.x + inner.w / 2, y: inner.y + inner.h / 2 };
  const inset = poster
    ? { ...core, bottom: Math.max(core.bottom, avail.bottom) + Math.round(24 * k) }
    : { top: to.y + BRIEF_PAD.y * k, left: to.x + BRIEF_PAD.x * k, right: W - (to.x + to.w) + BRIEF_PAD.x * k, bottom: H - (to.y + to.h) + fit.padBottom, ...(band ? { hideTitle: true } : {}), ...(fit.compact ? { compact: true } : {}), ...(fit.subCols ? { subCols: true } : {}), ...(fit.cells ? { cells: fit.cells } : {}) };
  const textMain = briefTextOn(scene.color);
  const baseColors: AtmosphereColors = titleColors ?? { bg: scene.color, panel: scene.color, accent: textMain, accentDim: textMain, accentGlow: textMain };
  const onColor = colorCutContentColors(baseColors, scene.color);
  const content = (slide.content ?? {}) as { title?: unknown; sub?: unknown; keyword?: string; keywordEmphasis?: KeywordEmphasisId; revealMode?: unknown };
  const kw = (text: string) => (content.keyword && !text.includes(content.keyword) ? content.keyword.replace(/ /g, '\u00A0') : content.keyword);
  // ⏱🔤 큰 제목이 한 자씩 시작하는 시각 — 그 낱말을 말하는 순간(2026-10-04 ①안). 없으면 종전(쓸림 끝).
  //   ★안 내용의 밝기 창(alpha)은 종전 lead 그대로★ — 바뀌는 건 제목 글자가 서기 시작하는 순간뿐이다.
  const titleLead = titleCueLeadSec(lead, wordCues?.title, fps, endSec);
  const tf = Math.round((t - titleLead) * fps); // 강조 낱말 효과의 프레임(글자가 다 선 뒤 발동)

  // 큰 제목(목록형 5종) — 한 자씩 · 두 줄이면 대구 카피.
  const bandGlued = band ? briefGlueLastWords(band.text, band.fontPx, band.width) : '';
  // 📐 #6394 마지막 줄이 한 어절이면 실제 글꼴로 재서 고르게 다시 나눈다(`title-balance.ts`) — 띠(대구 카피가 아닐 때)와 제목 장면 큰 제목.
  //   대구 카피(두 줄 · 둘째 줄 띠)는 이미 정해 나눈 모양이라 손대지 않는다. 훅은 조건 없이 부른다(빈 글 = 그대로).
  const [bandRef, bandBalanced] = useBalancedTitle(pair ? '' : bandGlued, { avail: 'self', keyword: content.keyword });
  const bandText = pair ? bandGlued : bandBalanced;
  const [heroRef, heroTitle] = useBalancedTitle(slide.type === 'fullText' ? str(content.title) : '', { avail: 'parent', keyword: content.keyword });
  // 체크 도장 시각 — 체크리스트 항목이 다 켜진 뒤(장면의 80% + 0.3초). 장면이 짧아 도장이 0.6초도 못 서 있으면 찍지 않는다(null).
  const stampAt = slide.type === 'checkList' && endSec !== undefined && typeof sceneFrames === 'number'
    ? ((at) => (at <= endSec - 0.6 ? at : null))(Math.max(lead + 1, (sceneFrames / fps) * 0.8 + 0.3, lastCueSec(wordCues?.items, fps) + 0.3))
    : null;
  const titleNode = band ? (() => {
    const emph = (text: string, done: number) => <KeywordText text={text} keyword={kw(text)} emphasis={content.keywordEmphasis} colors={onColor} f={tf - Math.round(done * fps) - 4} />;
    if (pair) {
      const p1 = colorCutKineticPlan(pair[0]);
      const at2 = titleLead + p1.lastStart + COLOR_CUT_PAIR_DELAY_SEC;
      return (
        <>
          <div><KineticChars text={pair[0]} tSec={t} at={titleLead}>{emph(pair[0], p1.done)}</KineticChars></div>
          <div><PairBand tSec={t} at={at2} bandColor={textMain} textColor={scene.color} padEm={0.18}>{pair[1]}</PairBand></div>
        </>
      );
    }
    return <KineticChars text={bandText} tSec={t} at={titleLead}>{emph(bandText, colorCutKineticPlan(bandText).done)}</KineticChars>;
  })() : null;

  // 제목 장면(fullText) — 무대가 직접 그린다: 큰 제목 한 자씩 + 부제는 띠 위 대구 카피. 등장 방식 «한꺼번에»면 부제가 같이, «차례로»면 반박 뒤.
  const fullText = slide.type === 'fullText' && str(content.title).trim() ? (() => {
    const innerW = (to.w - 2 * BRIEF_PAD.x * k);
    const heroPx = T.hero * k, bodyPx = T.body * k;
    // 마지막 두 어절은 무대 들머리에서 이미 붙였다(`colorCutGlueTitle` · 고아 방지 · 10-02 정본 검사 «고아» 226프레임).
    const title = heroTitle, sub = str(content.sub).trim();
    const plan = colorCutKineticPlan(title);
    const subAt = content.revealMode === 'all' ? titleLead : titleLead + plan.lastStart + COLOR_CUT_PAIR_DELAY_SEC;
    const subOneLine = sub ? briefTextWidth(sub, bodyPx, true) + bodyPx * 0.9 <= innerW : false;
    const subP = EASE_ENTER(lin(t, subAt, subAt + 0.3));
    const subCols = fit.subCols ? briefFullTextSubCols(sub) : null;
    return (
      <div style={{ position: 'absolute', top: inset.top, left: inset.left, right: inset.right, bottom: inset.bottom, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
        <div ref={heroRef} data-kinetic-title="" style={{ fontSize: heroPx, fontWeight: FW.bold, lineHeight: LINE_HEIGHT.tight, color: textMain, ...KO_TEXT, textWrap: 'balance' }}>
          <KineticChars text={title} tSec={t} at={titleLead}>
            <KeywordText text={title} keyword={kw(title)} emphasis={content.keywordEmphasis} colors={onColor} f={tf - Math.round(plan.done * fps) - 4} />
          </KineticChars>
        </div>
        {sub ? (
          // 📐 #6354 자리에 안 들어가면 무대가 «촘촘히(줄 높이 1.3)»·«두 열»을 정한다(briefStageFit) — 그대로 그린다. 그 밖은 종전.
          <div style={{ marginTop: 28 * k, fontSize: bodyPx, fontWeight: FW.semibold, lineHeight: fit.compact ? BRIEF_COMPACT.lineHeight : LINE_HEIGHT.normal, ...KO_TEXT }}>
            {subOneLine
              ? <PairBand tSec={t} at={subAt} bandColor={textMain} textColor={scene.color} padEm={0.45}>{sub}</PairBand>
              : <span style={{ display: 'inline-block', color: onColor.textSub, opacity: subP, transform: `translateY(${(0.6 * (1 - subP)).toFixed(4)}em)` }}>{subCols
                ? <span style={{ display: 'flex', justifyContent: 'center', gap: BRIEF_SUB_COL_GAP * k, textAlign: 'left' }}>{subCols.map((col, j) => <span key={j}><GlueText text={col} /></span>)}</span>
                : <GlueText text={sub} />}</span>}
          </div>
        ) : null}
      </div>
    );
  })() : null;

  // 큰 숫자 «쾅» — 안 내용이 1.12배에서 1배로 내려앉는다(소품 스프링).
  const slam = slide.type === 'bigNumber' ? 1 + (COLOR_CUT_SLAM_FROM - 1) * (1 - springStep(t - lead, UI_SPRING.stiffness, UI_SPRING.damping, UI_SPRING.mass)) : 1;
  const scale = fit.contentScale * slam * fill.s;
  const body = fullText ?? children; // 장표 글자색은 무대 맵(`motion-stages.tsx` contentColors)이 같은 장면색으로 정해 넘긴다

  // 체크 도장 — 글자는 없다. 안 내용 오른쪽 여백에 «쾅». 여백이 좁으면(긴 항목) 찍지 않는다(글자와 겹치지 않는다).
  const stampSpot = stampAt === null ? null
    : ((room) => (room >= COLOR_CUT_STAMP_ROOM * k ? { size: Math.min(200 * k, room * 0.8), cx: fill.rect.x + fill.rect.w + room / 2, cy: fill.rect.y + fill.rect.h * 0.62 } : null))(field.left + field.width - (fill.rect.x + fill.rect.w));
  const stamp = stampAt !== null && stampSpot ? (() => {
    const at = stampAt, { size, cx, cy } = stampSpot;
    const land = springStep(t - at, UI_SPRING.stiffness, UI_SPRING.damping, UI_SPRING.mass);
    // 2.4배에서 시작하되 원이 캔버스 밖으로 나가지 않는 배율까지만.
    const from = Math.max(1, Math.min(COLOR_CUT_STAMP_FROM, (Math.min(W - cx, cx, H - cy, cy) * 2) / size));
    const s = from - (from - 1) * land;
    return (
      <div data-colorcut-stamp="" style={{ position: 'absolute', left: cx - size / 2, top: cy - size / 2, width: size, height: size, borderRadius: '50%', background: textMain,
        opacity: Math.min(lin(t, at, at + 0.07), alpha), transform: `rotate(-8deg) scale(${s.toFixed(4)})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width={size * 0.56} height={size * 0.56} viewBox="0 0 24 24" fill="none"><path d="M4.5 12.5l5 5L19.5 7" stroke={scene.color} strokeWidth={3.2} strokeLinecap="round" strokeLinejoin="round" /></svg>
      </div>
    );
  })() : null;

  return (
    <AbsoluteFill data-colorcut-mode={scene.mode}>
      {scene.from ? <AbsoluteFill style={{ background: scene.from }} /> : null}
      <div data-colorcut-wipe="" style={{ position: 'absolute', left: 0, bottom: 0, width: W, height: Math.round(H * p), background: scene.color }} />
      <div data-brief-shape={slide.type} style={{ position: 'absolute', left: field.left, top: field.top, width: field.width, height: field.height }} />
      {band ? (
        <div ref={bandRef} data-brief-title="" style={{ position: 'absolute', left: band.left, top: band.top, width: band.width, textAlign: 'center', opacity: alpha,
          ...(fill.s !== 1 || fill.titleDy !== 0 || fill.dx !== 0 ? { transform: `translate(${fill.dx.toFixed(2)}px, ${fill.titleDy.toFixed(2)}px) scale(${fill.s.toFixed(4)})`, transformOrigin: 'center top' } : {}),
          fontSize: band.fontPx, fontWeight: FW.extrabold, lineHeight: LINE_HEIGHT.tight, color: textMain, ...KO_TEXT, textWrap: 'balance' }}>{titleNode}</div>
      ) : null}
      {stamp}
      <SlideStageInsetContext.Provider value={inset}>
        <AbsoluteFill data-brief-content="" style={{ opacity: alpha, ...(scale !== 1 || fill.bodyDy !== 0 || fill.dx !== 0
          ? { transform: `translate(${fill.dx.toFixed(2)}px, ${fill.bodyDy.toFixed(2)}px) scale(${scale.toFixed(4)})`, transformOrigin: `${origin.x.toFixed(2)}px ${origin.y.toFixed(2)}px` }
          : {}) }}>{body}</AbsoluteFill>
      </SlideStageInsetContext.Provider>
    </AbsoluteFill>
  );
};

/**
 * 🎨 나가는 무대 — 장표 장면 다음이 영상·그림 장면이면 앞 장면 색이 0.8초에 걸쳐 위로 쓸려 나간다(아랫변이 올라가며 영상이 아래부터 드러난다).
 *   `sceneIndex` 는 «지금 장면 번호»다 — 그때 그리는 앞 장면 색은 팔레트[(번호 − 1) % 4]. 글자는 이미 빠진 뒤다(0.1초 전).
 */
export const ColorCutExit: React.FC<{ f: number; prevSlide: BriefSlide; canvas?: string; brand: string; reserveRightPx?: number; prevCaption?: BriefCaption; split?: boolean; sceneIndex?: number }> = ({ f, prevSlide, brand, sceneIndex }) => {
  const { width: W, height: H, fps } = useVideoConfig();
  const p = colorCutWipe(Math.max(0, f) / fps, COLOR_CUT_WIPE_LONG_SEC);
  if (p >= 1) return null;
  const palette = colorCutPalette(brand);
  const color = colorCutScene(palette, { sceneIndex: typeof sceneIndex === 'number' ? sceneIndex - 1 : undefined, type: prevSlide.type }).color;
  return <div data-colorcut-exit="" style={{ position: 'absolute', left: 0, top: 0, width: W, height: Math.round(H * (1 - p)), background: color }} />;
};
